import { useEffect, useRef, useState } from "react";

/**
 * TARGET widget — matches the reference tweet aesthetic.
 *
 *   · Static targeting reticle: 4 `+` corners + center crosshair
 *   · Vertical `+` beam running full widget height through center
 *   · Horizontal dashed guide through center extending widget-wide
 *   · Two amber diamond "contacts" that jump between a handful of
 *     preset positions on a loop, each tagged with a lowercase `p`
 *   · Live coordinate readout ticking with the active target
 *
 * Everything is SVG so it stays crisp at any size.
 */
export function TargetWidget({
  width = 150,
  height = 70,
  label = "TARGET",
}: {
  width?: number;
  height?: number;
  label?: string;
}) {
  const cx = width / 2;
  const cy = height / 2;
  // Reticle half-size — distance from center to the corner `+` marks.
  const rx = Math.min(width * 0.18, 18);
  const ry = Math.min(height * 0.3, 14);

  // Pre-seeded slots the amber diamonds can occupy. Anchored relative
  // to the reticle so they always appear in a believable HUD position.
  const slots = useRef<Array<{ x: number; y: number }>>([
    { x: cx - rx * 2.2, y: cy - ry * 1.2 },
    { x: cx + rx * 2.2, y: cy - ry * 1.2 },
    { x: cx - rx * 2.2, y: cy + ry * 1.2 },
    { x: cx + rx * 2.2, y: cy + ry * 1.2 },
    { x: cx - rx * 1.1, y: cy - ry * 1.8 },
    { x: cx + rx * 1.1, y: cy + ry * 1.8 },
  ]);

  const [primaries, setPrimaries] = useState<number[]>([0, 3]);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [tick, setTick] = useState(0);

  // Reshuffle which slots hold the diamonds every ~1.6s.
  useEffect(() => {
    const id = window.setInterval(() => {
      setPrimaries(() => {
        const a = Math.floor(Math.random() * slots.current.length);
        let b = Math.floor(Math.random() * slots.current.length);
        if (b === a) b = (b + 1) % slots.current.length;
        return [a, b];
      });
    }, 1600);
    return () => window.clearInterval(id);
  }, []);

  // Coordinate readout + subtle scan-line flicker.
  useEffect(() => {
    const id = window.setInterval(() => {
      setTick((t) => (t + 1) % 10000);
      setCoords({
        x: (Math.random() - 0.5) * 400,
        y: (Math.random() - 0.5) * 100,
      });
    }, 320);
    return () => window.clearInterval(id);
  }, []);

  // Vertical `+` beam — render a `+` glyph every ~6px from top to bottom.
  const beamStep = 6;
  const beamYs: number[] = [];
  for (let y = 4; y < height - 2; y += beamStep) beamYs.push(y);

  // Horizontal dashed guide parameters.
  const hDashLen = 4;
  const hDashGap = 3;

  return (
    <div className="flex items-center gap-2 font-mono text-[9px]">
      <div className="uppercase tracking-[0.2em] text-accent2">{label}</div>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        aria-hidden="true"
      >
        {/* Horizontal dashed guide through center */}
        <line
          x1={0}
          y1={cy}
          x2={width}
          y2={cy}
          stroke="rgba(255,45,146,0.35)"
          strokeWidth={1}
          strokeDasharray={`${hDashLen} ${hDashGap}`}
        />

        {/* Vertical `+` beam — dotted column through center */}
        {beamYs.map((y) => (
          <text
            key={y}
            x={cx}
            y={y}
            fontSize={7}
            fill={
              // Subtle flicker along the beam so it feels alive.
              (Math.floor(tick / 2) + y) % 13 === 0
                ? "rgba(255,45,146,1)"
                : "rgba(255,45,146,0.75)"
            }
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily='"JetBrains Mono", monospace'
          >
            +
          </text>
        ))}

        {/* Reticle corner `+` marks */}
        {[
          { x: cx - rx, y: cy - ry },
          { x: cx + rx, y: cy - ry },
          { x: cx - rx, y: cy + ry },
          { x: cx + rx, y: cy + ry },
        ].map((p, i) => (
          <text
            key={i}
            x={p.x}
            y={p.y}
            fontSize={8}
            fill="rgba(255,45,146,0.85)"
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily='"JetBrains Mono", monospace'
          >
            +
          </text>
        ))}

        {/* Center crosshair — brighter */}
        <g>
          <line x1={cx - 4} y1={cy} x2={cx - 1} y2={cy} stroke="#ffffff" strokeWidth={1.2} />
          <line x1={cx + 1} y1={cy} x2={cx + 4} y2={cy} stroke="#ffffff" strokeWidth={1.2} />
          <line x1={cx} y1={cy - 4} x2={cx} y2={cy - 1} stroke="#ffffff" strokeWidth={1.2} />
          <line x1={cx} y1={cy + 1} x2={cx} y2={cy + 4} stroke="#ffffff" strokeWidth={1.2} />
          <circle
            cx={cx}
            cy={cy}
            r={1}
            fill="#ff2d92"
            style={{ filter: "drop-shadow(0 0 3px #ff2d92)" }}
          />
        </g>

        {/* Amber diamond contacts + `p` labels */}
        {primaries.map((slotIdx, i) => {
          const p = slots.current[slotIdx];
          if (!p) return null;
          return (
            <g
              key={`${slotIdx}-${i}`}
              style={{ animation: `fadeIn 0.25s ease-out both` }}
            >
              <g transform={`translate(${p.x}, ${p.y}) rotate(45)`}>
                <rect
                  x={-2.5}
                  y={-2.5}
                  width={5}
                  height={5}
                  fill="#ffb020"
                  style={{ filter: "drop-shadow(0 0 3px #ffb020)" }}
                />
              </g>
              <text
                x={p.x + 5}
                y={p.y + 4}
                fontSize={7}
                fill="#ffb020"
                fontFamily='"JetBrains Mono", monospace'
              >
                p
              </text>
            </g>
          );
        })}
      </svg>
      <div className="text-accent2 tabular-nums min-w-[80px]">
        {coords.x.toFixed(2)} {coords.y.toFixed(2)}
      </div>
    </div>
  );
}
